import React, { useState, useEffect, useContext } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import {
	Button,
	Box,
	Checkbox,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	CircularProgress,
	Select,
	InputLabel,
	MenuItem,
	FormControl,
	FormControlLabel,
	Switch,
	TextField,
	Tooltip,
	Typography,
	InputAdornment,
	IconButton
  } from '@material-ui/core';

import { Visibility, VisibilityOff, InfoOutlined } from '@material-ui/icons';

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";

import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import QueueSelect from "../QueueSelect";
import { AuthContext } from "../../context/Auth/AuthContext";
import { Can } from "../Can";
import useWhatsApps from "../../hooks/useWhatsApps";
// feat_340: fuente única de la lista de chips (antes había una copia a mano acá).
import { ALL_CHIPS_VISIBLE } from "../../helpers/getChipsVisibility";

const useStyles = makeStyles(theme => ({
	root: {
		display: "flex",
		flexWrap: "wrap",
	},
	multFieldLine: {
		display: "flex",
		"& > *:not(:last-child)": {
			marginRight: theme.spacing(1),
		},
	},

	btnWrapper: {
		position: "relative",
	},

	buttonProgress: {
		color: green[500],
		position: "absolute",
		top: "50%",
		left: "50%",
		marginTop: -12,
		marginLeft: -12,
	},
	formControl: {
		margin: theme.spacing(1),
		minWidth: 120,
	},
}));

const UserSchema = Yup.object().shape({
	name: Yup.string()
		.min(2, "Too Short!")
		.max(50, "Too Long!")
		.required("Required"),
	password: Yup.string().min(5, "Too Short!").max(50, "Too Long!"),
	email: Yup.string().email("Invalid email").required("Required"),
});

const UserModal = ({ open, onClose, onSuccess, userId }) => {
	const classes = useStyles();

	const initialState = {
		name: "",
		email: "",
		password: "",
		profile: "user",
		phone: "",
		canSeeUnassigned: false,
	};

	const { user: loggedInUser, setAuthUser } = useContext(AuthContext);

	const [user, setUser] = useState(initialState);
	const [selectedQueueIds, setSelectedQueueIds] = useState([]);
	const [allowedWhatsappIds, setAllowedWhatsappIds] = useState([]);
	const [showPassword, setShowPassword] = useState(false);
	const [whatsappId, setWhatsappId] = useState(false);
	const {loading, whatsApps} = useWhatsApps();
	// Chips visibility (feat_090) — la lista canónica vive en el helper (feat_340).
	const CHIPS_KEYS = Object.keys(ALL_CHIPS_VISIBLE);
	const [chipsVisibility, setChipsVisibility] = useState({ ...ALL_CHIPS_VISIBLE });

	// Signature fields (outside Formik to avoid Yup schema conflicts)
	const [signatureFeatureEnabled, setSignatureFeatureEnabled] = useState(false);
	const [signatureEnabled, setSignatureEnabled] = useState(false);
	const [signatureName, setSignatureName] = useState("");
	const [signatureShowName, setSignatureShowName] = useState(true);
	const [signatureShowDepartment, setSignatureShowDepartment] = useState(false);
	const [signatureShowCompany, setSignatureShowCompany] = useState(false);

	useEffect(() => {
		const fetchUser = async () => {
			// Load signatureEnabled company setting
			try {
				const { data: settings } = await api.get("/settings");
				const sigSetting = Array.isArray(settings) && settings.find(s => s.key === "signatureEnabled");
				setSignatureFeatureEnabled(sigSetting?.value === "enabled");
			} catch (_) {
				setSignatureFeatureEnabled(false);
			}

			if (!userId) return;
			try {
				const { data } = await api.get(`/users/${userId}`);
				setUser(prevState => ({ ...prevState, ...data }));
				const userQueueIds = data.queues?.map(queue => queue.id);
				setSelectedQueueIds(userQueueIds);
				setWhatsappId(data.whatsappId ? data.whatsappId : '');
				let parsedAllowed = [];
				if (data.allowedWhatsappIds) {
					try {
						const arr = JSON.parse(data.allowedWhatsappIds);
						if (Array.isArray(arr)) parsedAllowed = arr.map(Number).filter(Number.isFinite);
					} catch (_) {}
				}
				setAllowedWhatsappIds(parsedAllowed);
				// Load chips visibility (feat_090) — parse defensivo (MariaDB LONGTEXT devuelve string)
				let savedChips = data.ticketChipsVisibility;
				if (typeof savedChips === "string") {
					try { savedChips = JSON.parse(savedChips); } catch { savedChips = null; }
				}
				setChipsVisibility(savedChips && typeof savedChips === "object" && !Array.isArray(savedChips)
					? { ...ALL_CHIPS_VISIBLE, ...savedChips }
					: { ...ALL_CHIPS_VISIBLE });
				// Load signature fields
				setSignatureEnabled(!!data.signatureEnabled);
				setSignatureName(data.signatureName || "");
				setSignatureShowName(data.signatureShowName !== false);
				setSignatureShowDepartment(!!data.signatureShowDepartment);
				setSignatureShowCompany(!!data.signatureShowCompany);
			} catch (err) {
				toastError(err);
			}
		};

		fetchUser();
	}, [userId, open]);

	const handleClose = () => {
		onClose();
		setUser(initialState);
	};

	const buildSignaturePreview = () => {
		if (!signatureEnabled) return i18n.t("userModal.signaturePreviewDisabled");
		const parts = [];
		if (signatureShowName) {
			const displayName = signatureName.trim() || user?.name || "(nombre)";
			parts.push(displayName);
		}
		if (signatureShowDepartment) parts.push(i18n.t("userModal.signaturePreviewDept"));
		if (signatureShowCompany) parts.push(i18n.t("userModal.signaturePreviewCompany"));
		if (parts.length === 0) return i18n.t("userModal.signaturePreviewEmpty");
		return `*${parts.join(" - ")}:*`;
	};

	const SIGNATURE_FORBIDDEN_CHARS = /[*_~`]/;

	const handleSaveUser = async values => {
		let signatureNameClean = signatureName || "";
		let hadForbiddenChars = false;
		if (SIGNATURE_FORBIDDEN_CHARS.test(signatureNameClean)) {
			signatureNameClean = signatureNameClean.replace(/[*_~`]/g, "").trim();
			hadForbiddenChars = true;
		}

		const signatureData = signatureFeatureEnabled ? {
			signatureEnabled,
			signatureName: signatureNameClean || null,
			signatureShowName,
			signatureShowDepartment,
			signatureShowCompany,
		} : {};
		const userData = {
			...values,
			whatsappId,
			queueIds: selectedQueueIds,
			allowedWhatsappIds: allowedWhatsappIds.length > 0 ? JSON.stringify(allowedWhatsappIds) : null,
			...signatureData,
			ticketChipsVisibility: chipsVisibility
		};
		if (!userId && selectedQueueIds.length === 0) {
			toast.error("Debe asignar al menos una cola al usuario.");
			return;
		}
		try {
			if (userId) {
				const { data } = await api.put(`/users/${userId}`, userData);
				// Refresh AuthContext when editing own profile so signature changes
				// take effect immediately without requiring a browser reload.
				if (setAuthUser && data && Number(userId) === loggedInUser?.id) {
					setAuthUser(data);
				}
			} else {
				await api.post("/users", userData);
			}
			toast.success(i18n.t("userModal.success"));
			if (hadForbiddenChars) {
				toast.warning(i18n.t("userModal.signatureSanitizedWarning"));
			}
			if (onSuccess) onSuccess();
		} catch (err) {
			toastError(err);
		}
		handleClose();
	};

	return (
		<div className={classes.root}>
			<Dialog
				open={open}
				onClose={handleClose}
				maxWidth="xs"
				fullWidth
				scroll="paper"
			>
				<DialogTitle id="form-dialog-title">
					{userId
						? `${i18n.t("userModal.title.edit")}`
						: `${i18n.t("userModal.title.add")}`}
				</DialogTitle>
				<Formik
					initialValues={user}
					enableReinitialize={true}
					validationSchema={UserSchema}
					onSubmit={(values, actions) => {
						setTimeout(() => {
							handleSaveUser(values);
							actions.setSubmitting(false);
						}, 400);
					}}
				>
					{({ touched, errors, isSubmitting, values, setFieldValue }) => (
						<Form>
							<DialogContent dividers>
								<div className={classes.multFieldLine}>
									<Field
										as={TextField}
										label={i18n.t("userModal.form.name")}
										autoFocus
										name="name"
										error={touched.name && Boolean(errors.name)}
										helperText={touched.name && errors.name}
										variant="outlined"
										margin="dense"
										fullWidth
									/>
									<Field
										as={TextField}
										name="password"
										variant="outlined"
										margin="dense"
										label={i18n.t("userModal.form.password")}
										error={touched.password && Boolean(errors.password)}
										helperText={touched.password && errors.password}
										type={showPassword ? 'text' : 'password'}
										InputProps={{
										endAdornment: (
											<InputAdornment position="end">
											<IconButton
												aria-label="toggle password visibility"
												onClick={() => setShowPassword((e) => !e)}
											>
												{showPassword ? <VisibilityOff /> : <Visibility />}
											</IconButton>
											</InputAdornment>
										)
										}}
										fullWidth
									/>
								</div>
								<div className={classes.multFieldLine}>
									<Field
										as={TextField}
										label={i18n.t("userModal.form.email")}
										name="email"
										error={touched.email && Boolean(errors.email)}
										helperText={touched.email && errors.email}
										variant="outlined"
										margin="dense"
										fullWidth
									/>
									<FormControl
										variant="outlined"
										className={classes.formControl}
										margin="dense"
									>
										<Can
											role={loggedInUser.profile}
											perform="user-modal:editProfile"
											yes={() => (
												<>
													<InputLabel id="profile-selection-input-label">
														{i18n.t("userModal.form.profile")}
													</InputLabel>

													<Field
														as={Select}
														label={i18n.t("userModal.form.profile")}
														name="profile"
														labelId="profile-selection-label"
														id="profile-selection"
														required
													>
														<MenuItem value="admin">Admin</MenuItem>
														<MenuItem value="user">User</MenuItem>
													</Field>
												</>
											)}
										/>
									</FormControl>
								</div>
								<Can
									role={loggedInUser.profile}
									perform="user-modal:editQueues"
									yes={() => (
										<QueueSelect
											selectedQueueIds={selectedQueueIds}
											onChange={values => setSelectedQueueIds(values)}
										/>
									)}
								/>
								<Can
									role={loggedInUser.profile}
									perform="user-modal:editQueues"
									yes={() => (!loading &&
										<FormControl variant="outlined" margin="dense" className={classes.maxWidth} fullWidth>
											<InputLabel>{i18n.t("userModal.form.whatsapp")}</InputLabel>
											<Field
												as={Select}
												value={whatsappId}
												onChange={(e) => setWhatsappId(e.target.value)}
												label={i18n.t("userModal.form.whatsapp")}
											>
												<MenuItem value={''}>&nbsp;</MenuItem>
												{whatsApps.map((whatsapp) => (
													<MenuItem key={whatsapp.id} value={whatsapp.id}>{whatsapp.name}</MenuItem>
												))}
											</Field>
										</FormControl>
									)}
								/>
								<Can
									role={loggedInUser.profile}
									perform="user-modal:editProfile"
									yes={() => (
										values.profile !== "admin" && (
											<FormControlLabel
												style={{ marginTop: 4 }}
												control={
													<Switch
														size="small"
														color="primary"
														checked={!!values.canSeeUnassigned}
														onChange={e => setFieldValue("canSeeUnassigned", e.target.checked)}
														name="canSeeUnassigned"
													/>
												}
												label={i18n.t("userModal.form.canSeeUnassigned")}
											/>
										)
									)}
								/>
								{!loading && (
									<Can
										role={loggedInUser.profile}
										perform="user-modal:editProfile"
										yes={() => (
											values.profile !== "admin" && (
												<FormControl variant="outlined" margin="dense" fullWidth>
													<InputLabel>{i18n.t("userModal.form.allowedWhatsapps")}</InputLabel>
													<Select
														multiple
														value={allowedWhatsappIds}
														onChange={e => setAllowedWhatsappIds(e.target.value)}
														label={i18n.t("userModal.form.allowedWhatsapps")}
														renderValue={selected => {
															if (selected.length === 0) return i18n.t("userModal.form.allowedWhatsappsAll");
															return whatsApps
																.filter(w => selected.includes(w.id))
																.map(w => w.name)
																.join(", ");
														}}
													>
														{whatsApps.map(wa => (
															<MenuItem key={wa.id} value={wa.id}>
																<Checkbox checked={allowedWhatsappIds.indexOf(wa.id) > -1} />
																<span style={{ marginLeft: 4 }}>
																	{wa.name}
																	{(wa.phoneNumber || wa.number) && (
																		<span style={{ fontSize: 11, color: "#888", marginLeft: 6 }}>
																			{wa.phoneNumber || wa.number}
																		</span>
																	)}
																</span>
															</MenuItem>
														))}
													</Select>
													<span style={{ fontSize: 11, color: "#888", marginTop: 4, marginLeft: 2 }}>
														{i18n.t("userModal.form.allowedWhatsappsHelp")}
													</span>
												</FormControl>
											)
										)}
									/>
								)}
								<Field
									as={TextField}
									label="Teléfono (para alertas, ej: 5491112345678)"
									name="phone"
									variant="outlined"
									margin="dense"
									fullWidth
									placeholder="5491112345678"
								/>

								{signatureFeatureEnabled && (
									<Box mt={2}>
										<Divider />
										<Typography variant="subtitle2" style={{ marginTop: 12, marginBottom: 4 }}>
											{i18n.t("userModal.signatureSectionTitle")}
										</Typography>
										<FormControlLabel
											control={
												<Switch
													size="small"
													checked={signatureEnabled}
													onChange={e => setSignatureEnabled(e.target.checked)}
													color="primary"
												/>
											}
											label={i18n.t("userModal.signatureEnabledLabel")}
										/>
										{signatureEnabled && (
											<Box mt={1}>
												<TextField
													label={i18n.t("userModal.signatureNameLabel")}
													helperText={i18n.t("userModal.signatureNameHelp")}
													value={signatureName}
													onChange={e => setSignatureName(e.target.value)}
													fullWidth
													margin="dense"
													variant="outlined"
													size="small"
												/>
												<Box>
													<FormControlLabel
														control={<Checkbox size="small" checked={signatureShowName} onChange={e => setSignatureShowName(e.target.checked)} color="primary" />}
														label={<Typography variant="body2">{i18n.t("userModal.showName")}</Typography>}
													/>
												</Box>
												<Box>
													<FormControlLabel
														control={<Checkbox size="small" checked={signatureShowDepartment} onChange={e => setSignatureShowDepartment(e.target.checked)} color="primary" />}
														label={<Typography variant="body2">{i18n.t("userModal.showDepartment")}</Typography>}
													/>
												</Box>
												<Box>
													<FormControlLabel
														control={<Checkbox size="small" checked={signatureShowCompany} onChange={e => setSignatureShowCompany(e.target.checked)} color="primary" />}
														label={<Typography variant="body2">{i18n.t("userModal.showCompany")}</Typography>}
													/>
												</Box>
												<Box mt={1} p={1} style={{ backgroundColor: "#f0f7ff", borderRadius: 4, border: "1px solid #cce4ff" }}>
													<Typography variant="caption" color="textSecondary">
														{i18n.t("userModal.signaturePreviewLabel")}
													</Typography>
													<Typography variant="body2" style={{ fontFamily: "monospace", marginTop: 2 }}>
														{buildSignaturePreview()}
													</Typography>
												</Box>
											</Box>
										)}
									</Box>
								)}

								{/* feat_090 — Visibilidad de chips */}
								<Box mt={2}>
									<Divider />
									<Typography variant="subtitle2" style={{ marginTop: 12, marginBottom: 4 }}>
										{i18n.t("userModal.chipsVisibility.title")}
									</Typography>
									<Typography variant="body2" color="textSecondary" style={{ marginBottom: 8, fontSize: "0.8rem" }}>
										{i18n.t("userModal.chipsVisibility.subtitle")}
									</Typography>
									<div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "4px 8px" }}>
										{CHIPS_KEYS.map(chipKey => (
											<FormControlLabel
												key={chipKey}
												style={{ marginRight: 0 }}
												control={
													<Switch
														size="small"
														checked={!!chipsVisibility[chipKey]}
														onChange={e => {
															const v = e.target.checked;
															setChipsVisibility(prev => ({ ...prev, [chipKey]: v }));
														}}
														color="primary"
													/>
												}
												label={
													<span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: "0.85rem" }}>
														{i18n.t(`userModal.chipsVisibility.${chipKey}.label`)}
														<Tooltip title={i18n.t(`userModal.chipsVisibility.${chipKey}.tooltip`)} placement="top">
															<InfoOutlined style={{ fontSize: 14, color: "#9e9e9e" }} />
														</Tooltip>
													</span>
												}
											/>
										))}
									</div>
								</Box>
							</DialogContent>
							<DialogActions>
								<Button
									onClick={handleClose}
									color="secondary"
									disabled={isSubmitting}
									variant="outlined"
								>
									{i18n.t("userModal.buttons.cancel")}
								</Button>
								<Button
									type="submit"
									color="primary"
									disabled={isSubmitting}
									variant="contained"
									className={classes.btnWrapper}
								>
									{userId
										? `${i18n.t("userModal.buttons.okEdit")}`
										: `${i18n.t("userModal.buttons.okAdd")}`}
									{isSubmitting && (
										<CircularProgress
											size={24}
											className={classes.buttonProgress}
										/>
									)}
								</Button>
							</DialogActions>
						</Form>
					)}
				</Formik>
			</Dialog>
		</div>
	);
};

export default UserModal;
